import { z } from 'zod';
import { FeatureType } from '../core/types.js';
/**
 * Build one (uiName, ZodTypeAny) pair for a FeatureDef. When the def opts
 * in via `flatModelSchema`, that schema is used verbatim. Otherwise the
 * switch wraps `def.schema` in `.default(def.default)` so the field is
 * optional on the input side. The switch preserves per-branch type
 * correlation so we never need an `as never` cast when handing the entry
 * to z.object.
 */
function fieldEntry(def) {
    const { uiName } = def;
    if (def.flatModelSchema) {
        return [uiName, def.flatModelSchema];
    }
    switch (def.type) {
        case FeatureType.Boolean:
            return [uiName, def.schema.default(def.default)];
        case FeatureType.String:
            return [uiName, def.schema.default(def.default)];
        case FeatureType.Number:
            return [uiName, def.schema.default(def.default)];
        case FeatureType.Markdown:
            return [uiName, def.schema.default(def.default)];
        case FeatureType.MultiSelect:
            return [uiName, def.schema.default([...def.default])];
        case FeatureType.Json:
            return [uiName, def.schema.default(def.default)];
        case FeatureType.StringList:
            return [uiName, def.schema.default([...def.default])];
        default:
            return [uiName, z.unknown()];
    }
}
/**
 * Build a Zod object schema keyed by uiName. Replaces the hand-written
 * flatFeaturesModel: every field comes from a registry def. Defs that need
 * shapes outside the registry's primitives (nullable values, narrow enums,
 * preprocess pipelines) declare a `flatModelSchema` on the def itself.
 *
 * The optional `extras` map adds keys that aren't registry features (e.g.
 * synthetic computed fields). Both params are inferred `const` so the
 * returned ZodObject's shape tracks each field's exact schema type,
 * preserving autocomplete on `z.infer<typeof model>`.
 */
export function buildFlatFeaturesModel(defs, extras = {}) {
    const shape = {};
    for (const def of defs) {
        const [key, schema] = fieldEntry(def);
        shape[key] = schema;
    }
    for (const [key, schema] of Object.entries(extras)) {
        shape[key] = schema;
    }
    return z.object(shape);
}
